import React, { useEffect, useState } from "react";

import { useDispatch } from "react-redux";

import makeStyles from "@material-ui/core/styles/makeStyles";
import MuiDrawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";

// icons
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import SaveIcon from "@material-ui/icons/Save";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import CancelIcon from "@material-ui/icons/Cancel";

import "react-perfect-scrollbar/dist/css/styles.css";

import { PageFooter, Button, translate, showNotification } from "jazasoft";

import Table, { NativeTextInput } from "../../../components/Table";

import { defaultParsePaste, getDistinctObjectValues, getDistinctValues, isEmpty, listFromCsv, toFixed } from "../../../utils/helpers";
import { useDeepEffect, useDeepMemo, useMutation } from "../../../hooks";
import { getSortedSizeKeyList } from "../../../helpers/orderHelper";
import { REGEX_POSITIVE_NUMBER } from "../../../utils/regex";

const NON_SB_FIELDS = ["info", "totalPercent", "total"];

const useStyles = makeStyles(theme => ({
  drawer: ({ drawerWidth }) => ({
    width: drawerWidth,
    flexShrink: 0
  }),
  drawerPaper: ({ drawerWidth }) => ({
    width: drawerWidth
  }),
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center",
    margin: "1.5em"
  },
  content: {
    // padding: "0 1.5em"
  },
  table: {
    "& th, & td": {
      border: "1px solid rgba(224, 224, 224, 1)"
    }
  },
  rowCell: {
    padding: 0,
    fontSize: 12,
    "&:last-child": {
      padding: 0,
      fontSize: 12
    }
  }
}));

const BundlingQtyDrawer = props => {
  const { translate, open, order, selectedRow, bundleOrderParentKey, bundleOrder, bundleList, onDrawerClose, onRefresh } = props;
  const classes = useStyles({ drawerWidth: 1080 });

  const [breakupRows, setBreakupRows] = useState([]);
  const [editing, setEditing] = useState(false);

  const [mutate, { loading: saving }] = useMutation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!open) {
      setEditing(false);
    }
  }, [open]);

  const flowInfoList = useDeepMemo(() => {
    if (isEmpty(bundleOrder)) {
      return props.flowInfoList || [];
    }
    if (isEmpty(selectedRow?.flowInfoList)) return [];

    const flowInfoList = selectedRow?.flowInfoList
      .filter(fi => bundleOrder.styleList.includes(fi.style))
      .filter(fi => bundleOrder.colorList.includes(fi.color))
      .filter(fi => isEmpty(bundleOrder.destinations) || listFromCsv(bundleOrder.destinations).includes(fi.destination))
      .filter(fi => isEmpty(bundleOrder.deliveries) || listFromCsv(bundleOrder.deliveries).includes(fi.deliveryDate))
      .filter(fi => isEmpty(bundleOrder.delModes) || listFromCsv(bundleOrder.delModes).includes(fi.delMode))
      .filter(fi => isEmpty(bundleOrder.flowRefs) || listFromCsv(bundleOrder.flowRefs).includes(fi.flowRef))
      .filter(fi => isEmpty(bundleOrder.customerFlowRefs) || listFromCsv(bundleOrder.customerFlowRefs).includes(fi.customerFlowRef));

    return flowInfoList;
  }, [props.flowInfoList, selectedRow, bundleOrder]);

  useEffect(() => {
    setBreakupRows([]);
  }, [open]);

  const onBreakupChange =
    ({ rowIdx, column }) =>
    e => {
      const value = e?.target?.value || e?.value;

      setBreakupRows(prev => {
        let newRows = [...prev];
        let row;
        const data = defaultParsePaste(value) || [];
        if (data.length === 0 || (data.length === 1 && data[0].length === 1)) {
          // Restricting input of String value in size rows
          let allowInput = !isNaN(String(value)) || isEmpty(value) || REGEX_POSITIVE_NUMBER.test(value);
          if (allowInput) {
            if (column.dataKey === "totalPercent") {
              const orderQtyRow = prev[0];
              const bundleQty = Object.keys(orderQtyRow)
                .filter(key => !NON_SB_FIELDS.includes(key))
                .reduce((acc, key) => ({ ...acc, [key]: Math.round(value * orderQtyRow[key] * 0.01) }), {});
              row = { ...prev[rowIdx], ...bundleQty, [column.dataKey]: value };
              const total = Object.keys(row)
                .filter(key => !NON_SB_FIELDS.includes(key))
                .reduce((acc, key) => acc + (isEmpty(row[key]) ? 0 : Number(row[key])), 0);
              newRows.splice(rowIdx, 1, { ...row, total });
            } else {
              row = { ...prev[rowIdx], [column.dataKey]: value };
              const total = Object.keys(row)
                .filter(key => !NON_SB_FIELDS.includes(key))
                .reduce((acc, key) => acc + (isEmpty(row[key]) ? 0 : Number(row[key])), 0);
              newRows.splice(rowIdx, 1, { ...row, total });
            }
          }
        }

        return [...newRows];
      });
    };

  //** Prepare Size Breakup Columns**/
  const { columns: breakupColumns, headerRows } = useDeepMemo(() => {
    if (isEmpty(flowInfoList)) return { columns: [], headerRows: [] };

    const sizeBreakupList = flowInfoList.flatMap(fi => fi.sizeBreakupList?.filter(sb => sb.type === "ORDER") || []);

    const sbMap = sizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb }), {});
    const sbList = Object.values(sbMap).sort((a, b) => a.serialNo - b.serialNo);
    const editable = isEmpty(bundleOrder) || editing;

    const sizeKeys = getSortedSizeKeyList(sbList);
    let groupRow = {};
    let sizeList = [];
    let sizeGroup;
    for (let i = 0; i < sizeKeys.length; i++) {
      const sizeKey = sizeKeys[i];
      const sb = sbList.find(sb => sb.sizeKey === sizeKey);
      let leftBorder = false;
      if (sizeGroup !== sb.sizeGroup) {
        leftBorder = true;
        sizeGroup = sb.sizeGroup;
        groupRow[sizeKey] = sb.sizeGroup;
      }

      sizeList.push({
        dataKey: sizeKey,
        title: sizeKey.split("_")[1],
        leftBorder,
        element: (
          <NativeTextInput
            style={{ textAlign: "center" }}
            onChange={onBreakupChange}
            disabled={({ record, column }) =>
              !editable || ["Order Qty", "FPO Qty", "Bundled Qty"].includes(record.info) || column.dataKey === "total"
            }
          />
        )
      });
    }

    const haveSizeGroup = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 1;

    let columns = [
      {
        dataKey: "info",
        title: "Info",
        leftBorder: true
      },
      {
        dataKey: "totalPercent",
        title: "Total (%)",
        element: (
          <NativeTextInput
            style={{ textAlign: "center" }}
            onChange={onBreakupChange}
            disabled={({ record, column }) => !editable || ["Order Qty", "FPO Qty", "Bundled Qty"].includes(record.info)}
          />
        )
      }
    ];

    columns = [...columns, ...sizeList, { dataKey: "total", title: "Total", leftBorder: true }];
    columns = columns.map(c => ({
      ...c,
      align: "center",
      colSpan: ({ column, row, rowIdx }) =>
        !haveSizeGroup || rowIdx === 1 || NON_SB_FIELDS.includes(c.dataKey)
          ? 1
          : isEmpty(row[column.dataKey])
          ? 0
          : getDistinctValues(sbList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
    }));

    let headerRows = [];
    if (Object.keys(groupRow)?.length > 1) {
      headerRows = [
        groupRow,
        columns.reduce(
          (acc, c) => ({
            ...acc,
            [c.dataKey]: translate(`resources.orders.fields.flowInfo.${c.dataKey}`, {
              _: c.title
            })
          }),
          {}
        )
      ];
    } else {
      headerRows = [
        columns.reduce(
          (acc, c) => ({
            ...acc,
            [c.dataKey]: translate(`resources.orders.fields.flowInfo.${c.dataKey}`, {
              _: c.title
            })
          }),
          {}
        )
      ];
    }
    return { columns, headerRows };
  }, [flowInfoList, bundleOrder, editing]);

  //** Prepare Size Breakup Rows **/
  useDeepEffect(() => {
    if (isEmpty(flowInfoList)) return;

    const productIdList = getDistinctValues(flowInfoList.map(fi => fi.productId).filter(e => !isEmpty(e)));
    const productId = productIdList[0];

    const sizeBreakupList = flowInfoList
      .filter(fi => fi.productId === productId)
      .flatMap(fi => fi.sizeBreakupList?.filter(sb => sb.type === "ORDER").map(sb => ({ ...sb, qty: sb.qty / (fi.ratio || 1) })) || []);
    const fpoSizeBreakupList = flowInfoList
      .filter(fi => fi.productId === productId)
      .flatMap(fi => fi.fpoSizeBreakupList.map(sb => ({ ...sb, qty: sb.qty / (fi.ratio || 1) })) || []);

    const orderQtyMap = getDistinctObjectValues(sizeBreakupList)?.reduce(
      (acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }),
      {}
    );
    const orderQty = getDistinctObjectValues(sizeBreakupList)?.reduce((acc, sb) => acc + sb.qty, 0);
    const fpoQty = getDistinctObjectValues(fpoSizeBreakupList)?.reduce((acc, sb) => acc + sb.qty, 0);
    const fpoQtyMap = getDistinctObjectValues(fpoSizeBreakupList)?.reduce(
      (acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }),
      {}
    );
    
    let bundlingQty = fpoQty >= orderQty ? fpoQty : orderQty;
    let bundlingQtyMap = fpoQty >= orderQty ? fpoQtyMap : orderQtyMap;
    if (!isEmpty(bundleOrder)) {
      const boSizeBreakupList = bundleOrder?.boSizeBreakupList || [];
      bundlingQty = boSizeBreakupList.reduce((acc, sb) => acc + sb.qty, 0);
      bundlingQtyMap = boSizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }), {});
    }

    let rows = [
      {
        info: "Order Qty",
        total: orderQty,
        totalPercent: 100,
        ...orderQtyMap
      },
      {
        info: "FPO Qty",
        total: fpoQty,
        totalPercent: orderQty > 0 ? toFixed((fpoQty * 100) / orderQty) : 0,
        ...fpoQtyMap
      },
      {
        info: "Bundle Required Qty",
        total: bundlingQty,
        totalPercent: orderQty > 0 ? toFixed((bundlingQty * 100) / orderQty) : 0,
        ...bundlingQtyMap
      }
    ];

    if (editing && !isEmpty(bundleOrder)) {
      const boSizeBreakupList = bundleOrder?.boSizeBreakupList || [];
      const sizeKeys = getDistinctValues(boSizeBreakupList.map(sb => sb.sizeKey).filter(e => !isEmpty(e)));
      const emptyMap = Object.fromEntries(sizeKeys.map(sizeKey => [sizeKey, 0]));

      const boBundleList = bundleList?.filter(b => b.bundleOrderId === bundleOrder?.id) || [];
      const bundledQty = boBundleList.reduce((sum, b) => sum + b.qty, 0);
      const bundledQtyMap = sizeKeys.reduce(
        (acc, sizeKey) => ({
          ...acc,
          [sizeKey]: boBundleList.filter(b => b.sizeKey === sizeKey).reduce((sum, b) => sum + b.qty, 0)
        }),
        emptyMap
      );

      rows = [
        ...rows,
        {
          info: "Bundle Completed Qty",
          total: bundledQty,
          totalPercent: orderQty > 0 ? toFixed((bundledQty * 100) / orderQty) : 0,
          ...bundledQtyMap,
          ...(isEmpty(bundleList) && { total: 0, totalPercent: 0, ...emptyMap })
        }
      ];
    }

    setBreakupRows(rows);
  }, [flowInfoList, bundleOrder?.boSizeBreakupList, editing]);

  const onSubmit = () => {
    const bundlingQty = breakupRows.find(row => row.info === "Bundling Qty") || breakupRows[2];
    const sizeBreakupList = breakupColumns
      .filter(c => !NON_SB_FIELDS.includes(c.dataKey))
      .map((c, idx) => ({
        serialNo: idx + 1,
        type: "BUNDLE_ORDER",
        size: c.dataKey.substring(c.dataKey.indexOf("_") + 1),
        sizeGroup: c.dataKey.substring(0, c.dataKey.indexOf("_")),
        sizeKey: c.dataKey,
        qty: bundlingQty[c.dataKey] || 0
      }));

    const bundleQty = sizeBreakupList.reduce((acc, sb) => acc + sb.qty, 0);
    if (bundleQty <= 0) {
      dispatch(showNotification("Bundling qty is required", { type: "danger" }));
    }
    if (isEmpty(bundleOrder)) {
      const options = {
        url: `bundle-orders`,
        method: "post",
        params: { orderId: order?.id, bundleOrderParentKey },
        data: { sizeBreakupList, flowInfoList: flowInfoList.map(({ key, ...fi }) => fi) }
      };
      mutate(options, {
        onSuccess: () => {
          dispatch(showNotification("Bundle Order created successfully."));
          onRefresh && onRefresh();
          onDrawerClose();
        }
      });
    } else {
      //** Bundling Qty Validation (Bundling Qty cannot be less than bundled Qty Sizewise) **//
      const bundledQtyRow = breakupRows.find(row => row.info === "Bundled Qty") || breakupRows[3] || {};
      const sizeKeys = getDistinctValues(bundleOrder?.boSizeBreakupList?.map(sb => `${sb.sizeGroup}_${sb.size}`) || []);
      for (let i = 0; i < sizeKeys.length; i++) {
        const sizeKey = sizeKeys[i];
        const bundlingQty = sizeBreakupList.filter(sb => sb.sizeKey === sizeKey)?.reduce((acc, sb) => acc + sb.qty, 0) || 0;
        const bundledQty = bundledQtyRow[sizeKey] || 0;
        if (bundlingQty < bundledQty) {
          dispatch(
            showNotification(
              `Bundling Qty for Size Group = ${sizeKey.substring(0, sizeKey.indexOf("_"))} and size = ${sizeKey.substring(
                sizeKey.indexOf("_") + 1
              )} cannot be less than Bundled Qty = ${bundledQty}`,
              { type: "danger" }
            )
          );
          return;
        }
      }

      const options = {
        url: `bundle-orders/${bundleOrder.id}`,
        method: "put",
        data: { sizeBreakupList }
      };
      mutate(options, {
        onSuccess: () => {
          dispatch(showNotification("Bundle Order updated successfully."));
          onRefresh && onRefresh();
          onDrawerClose();
        }
      });
    }
  };

  const onDelete = () => {
    //** Validation: Delete Allowed only if bundled Qty is zero **//
    const boBundleList = bundleList?.filter(b => b.bundleOrderId === bundleOrder?.id);
    if (!isEmpty(boBundleList)) {
      dispatch(showNotification(`Bundle Order cannot be deleted.`, { type: "danger" }));
      return;
    }

    const options = {
      url: `bundle-orders`,
      method: "delete",
      params: { orderId: order?.id, bundleOrderId: bundleOrder?.id }
    };
    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("Bundle Order deleted successfully."));
        onRefresh && onRefresh();
        onDrawerClose();
      }
    });
  };

  return (
    <MuiDrawer
      className={classes.drawer}
      anchor="right"
      open={open}
      onClose={onDrawerClose}
      classes={{
        paper: classes.drawerPaper
      }}>
      <div className={classes.drawerHeader}>
        <IconButton onClick={onDrawerClose}>
          <ChevronRightIcon />
        </IconButton>

        <Typography style={{ marginLeft: "0.5em" }} variant="h5">
          Bundle Required Qty
        </Typography>
      </div>
      {/* <Divider /> */}
      <div className={classes.content}>
        <div>
          <Table
            classes={{ table: classes.table, rowCell: classes.rowCell }}
            columns={breakupColumns}
            headerRows={headerRows}
            rows={breakupRows}
            i18nKey="resources.orders.fields.flowInfo"
            headerCellStyle={column => ({
              width: NON_SB_FIELDS.includes(column?.dataKey) ? 100 : undefined,
              borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
              fontWeight: "bold",
              backgroundColor: "#C6D0DA",
              textAlign: column.dataKey === "info" ? "left" : "center"
            })}
            cellStyle={({ column, record }) => ({
              fontWeight: record.isTotalRow ? "bold" : "normal",
              borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
              textAlign: column.dataKey === "info" ? "left" : "center",
              paddingLeft: column.dataKey === "info" ? 8 : 0
            })}
          />

          {/* Footer */}
          <PageFooter className={classes.pageFooter}>
            {(isEmpty(bundleOrder) || editing) && (
              <Button
                style={{ marginLeft: "1em" }}
                label={translate("resources.orders.action.save", { _: "SAVE" })}
                color="primary"
                variant="contained"
                disabled={saving}
                onClick={onSubmit}>
                <SaveIcon />
                {saving && <CircularProgress size={20} thickness={2} />}
              </Button>
            )}
            {!isEmpty(bundleOrder) && !editing && (
              <Button
                style={{ marginLeft: "1em" }}
                label={translate("resources.orders.action.edit", { _: "EDIT" })}
                color="primary"
                variant="outlined"
                disabled={saving}
                onClick={() => setEditing(true)}>
                <EditIcon />
                {saving && <CircularProgress size={20} thickness={2} />}
              </Button>
            )}
            {!isEmpty(bundleOrder) && !editing && (
              <Button
                style={{ marginLeft: "1em", color: "red", borderColor: "red" }}
                label={translate("resources.orders.action.delete", { _: "DELETE" })}
                variant="outlined"
                disabled={saving}
                onClick={onDelete}>
                <DeleteIcon />
                {saving && <CircularProgress size={20} thickness={2} />}
              </Button>
            )}

            {(isEmpty(bundleOrder) || editing) && (
              <Button
                style={{ marginLeft: "1em" }}
                label={translate("resources.orders.action.cancel", { _: "CANCEL" })}
                color="primary"
                variant="outlined"
                disabled={saving}
                onClick={onDrawerClose}>
                <CancelIcon />
              </Button>
            )}
          </PageFooter>
        </div>
      </div>
    </MuiDrawer>
  );
};

export default translate(BundlingQtyDrawer);
